import { TabItems } from "@components/common";

import { CreateEducationalEvent } from "@feature/create-educational-event";
import { CreateEducationalPromotion } from "@feature/create-educational-promotion";
import { MaterialsPublicTab } from "@feature/study-materials/materials-public-tab";
import { EducationalEventPage } from "@feature/study-materials/educational-event-page";
import { StudyCenter } from "@feature/study-center";
import { StudyCenterMaterials } from "@feature/study-materials";
import { EducationalPromotionPage } from "@feature/study-materials/educational-promotion-page";

import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import {
  $UserAddPermissions,
  ACTIONS,
  UserPriorityEnum,
} from "@store/user-store";
import { useUnit } from "effector-react";
import { TitleProps } from "@store/title-store";
import { setTitle } from "@store/title-store";

import { PageTabPropsT } from "@interfaces/common";
import { ColorfulButton } from "@components/common/common";
import { EducationalEventsTab } from "@feature/study-materials/educational-events-tab";
import { EducationalPromotionsTab } from "@feature/study-materials/educational-promotions-tab";
import styles from "@scss/pages/study-center.module.scss";
import { CreatePublicArticle } from "@feature/study-materials/create-public-article";
import { CreatePublicPostlink } from "@feature/study-materials/create-public-postlink";
import { PublicMaterialPage } from "@feature/study-materials/public-material-page";
import { EditPublicArticle } from "@feature/study-materials/edit-public-article";
import ControlRequestTrainingPage from "@pages/control-request-training-page/control-request-training-page";
import InternalList from "@feature/study-center/internal/list";
import InternalListPage from "@feature/study-center/internal/list-page";
import InternalComission from "@feature/study-center/internal/comission";
import InternalProgram from "@feature/study-center/internal/program";
import InternalCourses from "@feature/study-center/internal/courses";
import InternalStudents from "@feature/study-center/internal/students";

const pathnameToTitle: { [key: string]: string } = {
  "/study-center/internal": "Внутреннее обучение",
  "/study-center/public-materials": "Материалы для внутреннего обучения",
  "/study-center/materials": "Предложения месяца",
};

const isEducationPormotionsPage = (pathname: string): boolean => {
  return /\/study-center\/materials\/educational-promotions/i.test(pathname);
};

const isCreateEducationalPromotionPage = (pathname: string): boolean => {
  return pathname === "/study-center/materials/educational-promotions/create";
};

const isUpdateEducationalPromotionPage = (pathname: string): boolean => {
  return /\/study-center\/materials\/educational-promotions\/edit\/[0-9]+/i.test(pathname);
};

const isViewEducationalPromotionPage = (pathname: string): boolean => {
  return /\/study-center\/materials\/educational-promotions\/[0-9]+/i.test(pathname);
};

const isInternalListEditPage = (pathname: string): boolean => {
  return /\/study-center\/internal\/list\/[0-9]+/i.test(pathname);
};

const isCreateEventPage = (pathname: string): boolean => {
  return pathname === "/study-center/materials/educational-events/create";
};

const isUpdateEventPage = (pathname: string): boolean => {
  return /\/study-center\/materials\/educational-events\/edit\/[0-9]+/i.test(pathname);
};

const isViewEventPage = (pathname: string): boolean => {
  return /\/study-center\/materials\/educational-events\/[0-9]+/i.test(pathname);
};

const getEducationalPromotionPageTitle = (
  pathname: string
): string | undefined => {
  if (isCreateEducationalPromotionPage(pathname)) {
    return "Создание акции или предложения";
  }

  if (isUpdateEducationalPromotionPage(pathname)) {
    return "Редактирование акции или предложения";
  }

  if (isCreateEventPage(pathname)) {
    return "Создание события или вебинара";
  }

  if (isUpdateEventPage(pathname)) {
    return "Редактирование события или вебинара";
  }
};

const StudyCenterRoutes = () => {
  const { pathname, search } = useLocation();
  const permissions = useUnit($UserAddPermissions);

  const [titleChild, setTitleChild] = useState<string>();

  const TAB_ITEMS: PageTabPropsT[] = [
    { path: "/study-center", label: "Внешнее обучение", exact: true },
    {
      path: "/study-center/internal",
      label: "Внутреннее обучение",
    },
    {
      path: "/study-center/public-materials",
      label: "Материалы для внутреннего обучения",
    },
    {
      path: "/study-center/materials",
      label: "Предложения месяца",
      btn: isEducationPormotionsPage(pathname) && permissions.hasPermission(ACTIONS.educationalCenter_materials_allowedToCreate) ? 
        <ColorfulButton
          link="/study-center/materials/educational-promotions/create"
          text="Создать акцию или предложение"
        /> : permissions.hasPermission(ACTIONS.educationalCenter_materials_allowedToCreate) ? 
        <ColorfulButton
          link="/study-center/materials/educational-events/create"
          text="Создать событие или вебинар"
        /> : undefined
    },
  ];

  useEffect(() => {
    const objPathnameToTitle = Object.keys(pathnameToTitle)
      .sort((a, b) => a.length - b.length)
      .reverse()
      .find((e) => pathname.indexOf(e) !== -1);
    const newTitle =
      pathnameToTitle[pathname] ?? pathnameToTitle[objPathnameToTitle!];

    const title = [
      { title: "Главная", href: "/" },
      { title: "Учебный центр", href: "/study-center" },
    ] as TitleProps[];

    if (pathname !== "/study-center" && newTitle) {
      title.push({ title: newTitle, href: objPathnameToTitle ?? pathname });
    }
    if (pathname === "/study-center") {
      title.push({ title: "Формирование заявок на обучение", href: pathname });
    } else if (pathname === "/study-center/request-training") {
      title.push({ title: "Заявки на обучение", href: pathname });
    }
    if (pathname.indexOf("study-center/internal") !== -1) {
      switch (pathname.split("/").pop()) {
        case "list":
          title.push({
            title: "Список обучений",
            href: `/study-center/internal/list`,
          });
          break;
        case "students":
          title.push({
            title: "Список обучающихся",
            href: `/study-center/internal/students`,
          });
          break;
        case "comission":
          title.push({
            title: "Комиссии",
            href: `/study-center/internal/comission`,
          });
          break;
        case "programs":
          title.push({
            title: "Программы обучений",
            href: `/study-center/internal/programs`,
          });
          break;
        case "courses":
          title.push({
            title: "Курсы",
            href: `/study-center/internal/courses`,
          });
          break;
      }
    } else if (pathname.indexOf("study-center/materials") !== -1) {
      switch (pathname.split("/").pop()) {
        case "educational-promotions":
          title.push({
            title: "Акции и предложения",
            href: `/study-center/materials/educational-promotions`,
          });
          break;
        case "educational-events":
          title.push({
            title: "События и вебинары",
            href: `/study-center/materials/educational-events`,
          });
          break;
      }
    }
    const titleCreateOrEditPage = getEducationalPromotionPageTitle(pathname);
    if (titleCreateOrEditPage) {
      title.push({ title: titleCreateOrEditPage, href: pathname });
    }
    if (
      isInternalListEditPage(pathname) ||
      isViewEducationalPromotionPage(pathname) ||
      isViewEventPage(pathname)
    ) {
      const id = pathname
        .split("/")
        .filter((e) => parseInt(e))
        .shift();
      if (id && titleChild) title.push({ title: titleChild, href: pathname });
    }

    setTitle(title);
  }, [pathname, search, titleChild]);

  if (
    !permissions.hasPermission(
      ACTIONS.educationalCenter_internalTraining_comissions_allowedToShow
    ) ||
    !permissions.hasPermission(
      ACTIONS.educationalCenter_internalTraining_list_allowedToShow
    )
  )
    delete TAB_ITEMS[1];

  return (
    <div className={styles.StudyCenter}>
      <div className={styles.StudyCenter__panel}>
        <TabItems items={TAB_ITEMS} />
        <Routes>
          <Route path="/" Component={StudyCenter} />
          {permissions.hasPermission(ACTIONS.companyApplicationsForTraining_allowedToShow) && (
            <Route path="/request-training" Component={ControlRequestTrainingPage} />
          )}
          <Route path="/materials" element={<Navigate to="/study-center/materials/educational-promotions" />} />
          <Route path="/internal" element={
            permissions.hasPermission(ACTIONS.educationalCenter_internalTraining_list_allowedToShow) ?
              <Navigate to="/study-center/internal/list" /> :
            (!permissions.hasPermission(ACTIONS.educationalCenter_internalTraining_list_allowedToShow) &&
            permissions.hasPermission(ACTIONS.educationalCenter_internalTraining_comissions_allowedToShow)) && 
              <Navigate to="/study-center/internal/comission" />
          } />
          {permissions.hasPermission(ACTIONS.educationalCenter_internalTraining_list_allowedToShow) && (
            <Route path="/internal/list" Component={InternalList} />
          )}
          {permissions.hasPermission(ACTIONS.educationalCenter_internalTraining_list_allowedToShow) && (
            <Route path="/internal/list/:id" element={<InternalListPage setTitle={setTitleChild} />} />
          )}
          {permissions.hasPermission(ACTIONS.educationalCenter_internalTraining_comissions_allowedToShow) && (
            <Route path="/internal/comission" Component={InternalComission} />
          )}
          {permissions.hasPermission(ACTIONS.educationalCenter_internalTraining_programs_allowedToShow) && (
            <Route path="/internal/programs" Component={InternalProgram} />
          )}
          {permissions.hasRequiredLevel(UserPriorityEnum.Owner) && (
            <Route path="/internal/courses" Component={InternalCourses} />
          )}
          {permissions.hasPermission(ACTIONS.educationalCenter_internalTraining_list_allowedToShow) && (
            <Route path="/internal/students" Component={InternalStudents} />
          )}
          <Route path="/materials/educational-promotions" element={
            <StudyCenterMaterials>
              <EducationalPromotionsTab />
            </StudyCenterMaterials>
          } />
          <Route path="/materials/educational-events" element={
            <StudyCenterMaterials>
              <EducationalEventsTab />
            </StudyCenterMaterials>
          }/>
          <Route path="/public-materials" element={
            <StudyCenterMaterials viewLink={false}>
              <MaterialsPublicTab />
            </StudyCenterMaterials>
          }/>
          <Route path="/public-materials/:sectionId/:categoryId/:id" Component={PublicMaterialPage}/>
          <Route path="/public-materials/:sectionId/:categoryId/article/create" element={<CreatePublicArticle type="create" />} />
          <Route path="/public-materials/:sectionId/:categoryId/post-link/create" element={<CreatePublicPostlink type="create" />} />
          <Route path="/public-materials/:sectionId/:categoryId/:id/article/edit" Component={EditPublicArticle} />
          <Route path="/materials/educational-promotions/create" Component={CreateEducationalPromotion} />
          <Route path="/materials/educational-events/create" Component={CreateEducationalEvent} />
          <Route path="/materials/educational-promotions/:id" element={<EducationalPromotionPage setTitle={setTitleChild} />} />
          <Route path="/materials/educational-events/:id" element={<EducationalEventPage setTitle={setTitleChild} />} />
          <Route path="/materials/educational-promotions/edit/:id" Component={CreateEducationalPromotion}/>
          <Route path="/materials/educational-events/edit/:id" Component={CreateEducationalEvent} />
        </Routes>
      </div>
    </div>
  );
};

export default StudyCenterRoutes;
